// Post detail route. Deep-link target for notifications and shareable
// post URLs.
//
// Chrome is rendered from first paint through fully loaded: the header
// card (author identity) and the post body card swap from skeleton stand-ins
// to real content as each query slice resolves, never gating behind a
// fullscreen loading return. Skeleton sub-components are co-located below
// — no parallel `*Skeleton.tsx` file.

import { createFileRoute, notFound } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslations } from '@/i18n/translations';
import { useQuery } from '@tanstack/react-query';
import { isUuid } from '@leanin/shared';
import {
  Caption,
  Card,
  DelayedSkeleton,
  Notice,
  PageHeader,
  PersonRowSkeleton,
  Row,
  Skeleton,
  SkeletonText,
  Stack,
  useDelayedLoading,
} from '@leanin/ui';
import { DetailPage } from '@/components/page-shell/index';
import { PostCard } from '@/components/feed/PostCard';
import { PostEntityCardSkeleton } from '@/components/feed/PostEntityCard.skeleton';
import { feedErrorMessage } from '@/components/feed/feed-error-copy';
import { useErrorMessage } from '@/lib/error-message';
import type { FeedPost } from '@/components/feed/types';
import { feedPostAuthor } from '@/components/feed/feed-item-to-post';
import { MemberAvatar, memberDisplayName } from '@/components/MemberIdentity';
import { useFeaturePost, useUnfeaturePost } from '@/lib/mutations/feed';
import { useMember } from '@/lib/use-session';
import { useFormatDate } from '@/lib/format-date.client';
import { feedKeys } from '@/lib/queries/feed';
import { isNotFoundApiError } from '@/lib/api-error';
import { NotFoundPage } from '@/components/shell/NotFoundPage';

type PostDetailSearch = {
  /** Comment deep link (`?comment=<uuid>`) — auto-opens the thread and
   *  scrolls the comment into view. Invalid values are dropped. */
  comment?: string;
};

export const Route = createFileRoute('/_authed/posts/$id')({
  validateSearch: (raw: Record<string, unknown>): PostDetailSearch => ({
    ...(typeof raw.comment === 'string' && isUuid(raw.comment)
      ? { comment: raw.comment }
      : {}),
  }),
  beforeLoad: ({ params }) => {
    if (!isUuid(params.id)) throw notFound();
  },
  component: PostDetailPage,
});

function PostDetailPage() {
  const t = useTranslations('Feed');
  const tCommon = useTranslations('Common');
  const describeError = useErrorMessage();
  const fmt = useFormatDate();
  const { id } = Route.useParams();
  const { comment } = Route.useSearch();

  const isPlatformAdmin = useMember()?.platformRole === 'admin';
  const featureMutation = useFeaturePost();
  const unfeatureMutation = useUnfeaturePost();
  // Same per-action error surface as FeedView's feature wiring — the menu
  // items give no feedback of their own.
  const [featureError, setFeatureError] = useState<string | null>(null);

  const postQuery = useQuery({
    ...feedKeys.detail(id),
    retry: false,
  });

  const post = postQuery.data ?? null;
  // The post carries its author as the viewer may see them: a member who is
  // no longer active is a deleted member (no profile to fetch).
  const author = post !== null ? feedPostAuthor(post.author) : null;
  const showHeaderSkeleton = useDelayedLoading(post === null);

  // Post fetch returns `null` on 404 — the post is permanently unreachable,
  // so render the branded 404 directly (render-context `throw notFound()`
  // lands in TanStack's catch-boundary instead of its 404 surface in this
  // version). Placed AFTER all hooks so the hook count stays stable across
  // renders.
  if (
    isNotFoundApiError(postQuery.error) ||
    (postQuery.data === null && !postQuery.isLoading)
  ) {
    return <NotFoundPage />;
  }
  if (postQuery.isError) throw notFound();

  // A post that is not live has no "posted" moment: say when it is scheduled
  // for, or that it was not published — in the header and the card alike.
  const whenLabel =
    post === null
      ? null
      : post.status === 'published'
        ? fmt.relativeShort(post.createdAt)
        : post.status === 'scheduled' && post.publishAt !== null
          ? t('post.scheduledFor', {
              date: fmt.medium(post.publishAt),
              time: fmt.timeShort(post.publishAt),
            })
          : t('post.notPublished');

  const feedPost: FeedPost | null =
    post !== null && author !== null && whenLabel !== null
      ? {
          id: post.id,
          author,
          time: whenLabel,
          body: post.body,
          bodyFormat: post.bodyFormat,
          mentions: post.mentions ?? [],
          reactionCounts: post.reactionCounts,
          viewerReaction: post.viewerReaction,
          viewerSaved: post.viewerSaved,
          comments: post.commentCount,
          attachedEntities: post.attachedEntities,
          media: post.media as FeedPost['media'],
          linkPreviews: post.linkPreviews,
          poll: post.poll,
          isGlobal: post.isGlobal,
          scopes: post.scopes,
          viewerCan: post.viewerCan,
          removals: post.removals,
          featuredAt: post.featuredAt,
          // The detail page renders the full thread, never the preview.
          previewComment: null,
        }
      : null;

  const header =
    feedPost !== null && post !== null ? (
      <PageHeader
        leading={<MemberAvatar member={feedPost.author} size={72} />}
        title={memberDisplayName(feedPost.author, tCommon)}
        titleColor={feedPost.author.kind === 'deleted' ? 'mute' : 'foreground'}
        {...(feedPost.author.kind === 'member' && feedPost.author.role
          ? { subtitle: feedPost.author.role }
          : {})}
        meta={
          <Caption>
            {post.status === 'published'
              ? t('post.posted', { when: feedPost.time })
              : feedPost.time}
          </Caption>
        }
      />
    ) : showHeaderSkeleton ? (
      <PageHeader
        leading={<Skeleton width={72} height={72} rounded="full" />}
        title={<Skeleton width={220} height={32} rounded="sm" />}
        preview={<Skeleton width={260} height={24} rounded="sm" />}
        meta={<Skeleton width={120} height={18} rounded="sm" />}
      />
    ) : null;

  return (
    <DetailPage header={header}>
      <DelayedSkeleton
        isLoading={feedPost === null}
        fallback={<PostBodySkeleton />}
      >
        {feedPost !== null && post !== null ? (
          <Stack gap="sm">
            {/* Only the author (and admin oversight) can open a post that is
                not live; say so, and for a failed one say why. */}
            {post.status === 'failed' && post.publishFailureCode !== null ? (
              <Notice
                intent="warning"
                placement="inline"
                title={t(`scheduled.failedReason.${post.publishFailureCode}`)}
                dismissible={false}
              >
                {t('scheduled.failedBody')}
              </Notice>
            ) : post.status === 'scheduled' ? (
              <Notice
                intent="info"
                placement="inline"
                title={t('post.scheduledNotice')}
                dismissible={false}
              />
            ) : null}
            <PostCard
              // Keyed by post id: the route component never remounts on a
              // param change, and PostCard mirrors the post's reaction /
              // save / comment state into local state at mount. A warm-cache
              // post→post hop (attached-post card, then Back) would
              // otherwise render this post with the other post's counts —
              // and write reactions onto the wrong post.
              key={feedPost.id}
              post={feedPost}
              fullBody
              {...(comment !== undefined
                ? { initialCommentsOpen: true, highlightCommentId: comment }
                : {})}
              {...(isPlatformAdmin
                ? {
                    onFeature: () => {
                      setFeatureError(null);
                      featureMutation.mutate(
                        { postId: feedPost.id },
                        {
                          onError: (err) =>
                            setFeatureError(
                              feedErrorMessage(err, describeError, t, 'errors.codes.CONFLICT'),
                            ),
                        },
                      );
                    },
                    onUnfeature: () => {
                      setFeatureError(null);
                      unfeatureMutation.mutate(
                        { postId: feedPost.id },
                        {
                          onError: (err) =>
                            setFeatureError(
                              feedErrorMessage(err, describeError, t, 'errors.codes.CONFLICT'),
                            ),
                        },
                      );
                    },
                  }
                : {})}
            />
            {featureError !== null ? (
              <Notice
                intent="danger"
                placement="inline"
                title={featureError}
                dismissible
                onDismiss={() => setFeatureError(null)}
              />
            ) : null}
          </Stack>
        ) : null}
      </DelayedSkeleton>
    </DetailPage>
  );
}

// Body skeleton for the PostCard. Co-located rather than parallel-filed —
// mirrors PostCard's structure (MemberRow, scope pills, body text, attached
// entity, engagement footer) so the loading and loaded layouts share
// rhythm.
function PostBodySkeleton() {
  return (
    <Card pad="flush" style={{ overflow: 'hidden' }}>
      <Stack gap="lg" p="xl">
        <PersonRowSkeleton avatarSize={44} />
        <Row gap="sm" align="center">
          <Skeleton width={88} height={24} rounded="full" />
          <Skeleton width={104} height={24} rounded="full" />
        </Row>
        <SkeletonText lines={5} size="md" />
        <PostEntityCardSkeleton />
      </Stack>
      <Row
        py="md"
        px="xl"
        align="center"
        justify="space-between"
        style={{ borderTop: '1px solid var(--border)' }}
      >
        <Row gap="sm" align="center">
          <Skeleton width={60} height={28} rounded="md" />
          <Skeleton width={96} height={28} rounded="md" />
        </Row>
        <Row gap="xs" align="center">
          <Skeleton width={28} height={28} rounded="md" />
          <Skeleton width={28} height={28} rounded="md" />
        </Row>
      </Row>
    </Card>
  );
}
